/** @file The selected IK constraint's fields: chain, target, order, mix, softness and the four flags. */
import type { Id, IkConstraintDoc } from '@pose2d/format/types';
import { Checkbox } from '../../ui-kit/Checkbox';
import { NumberField } from '../../ui-kit/NumberField';
import { Select } from '../../ui-kit/Select';
import { boneOptions } from '../rigKit/boneOptions';
import type { RigView } from '../rigKit/rigView';
import { TargetLine } from '../rigKit/TargetLine';
import { IkChain } from './IkChain';
import { ikChainBones } from './ikChainBones';
import { ikEditCall, type IkField } from './ikEditCall';
import { ikShown } from './ikShown';

/** Props of IkFields: the rig view plus the constraint being edited. */
export interface IkFieldsProps {
  view: RigView;
  id: Id;
  ik: IkConstraintDoc;
}

/** Keyable fields key at the playhead while animating (TargetLine says which); chain, target, order and uniform edit setup. */
export function IkFields({ view, id, ik }: IkFieldsProps) {
  const { doc, t, run, target } = view;
  const { values, keyed } = ikShown(doc, id, ik, target);
  const edit = (field: IkField, value: number | boolean | string): void => { run([ikEditCall(id, field, value, target)]); };
  const bones = boneOptions(doc, (b) => b.kind === undefined);
  const setChain = (end: string, length: 1 | 2): void => { const chain = ikChainBones(doc, end, length); if (chain !== null) run([{ name: 'ik.set', input: { target: id, value: { bones: chain } } }]); };
  return (
    <fieldset className="rig-fields">
      <legend>{ik.name}</legend>
      <IkChain t={t} bones={bones} end={ik.bones[ik.bones.length - 1] ?? ''} length={ik.bones.length} onChange={setChain} />
      <Select label={t('panels.constraints.target')} value={ik.target} options={bones} onChange={(bone) => { edit('target', bone); }} />
      <NumberField label={t('panels.constraints.order')} value={ik.order} min={0} step={1} precision={0} onCommit={(v) => { edit('order', v); }} />
      <Checkbox label={t('panels.constraints.uniform')} checked={ik.uniform} onChange={(v) => { edit('uniform', v); }} />
      <TargetLine t={t} target={target} {...(keyed.length > 0 ? { extra: t('panels.constraints.keyedHere', { fields: keyed.join(', ') }) } : {})} />
      <NumberField label={t('panels.constraints.mix')} value={values.mix} min={0} max={1} step={0.01} precision={3} onCommit={(v) => { edit('mix', v); }} />
      <NumberField label={t('panels.constraints.softness')} value={values.softness} min={0} step={0.1} precision={2} onCommit={(v) => { edit('softness', v); }} />
      <Checkbox label={t('panels.constraints.bendPositive')} checked={values.bendPositive} onChange={(v) => { edit('bendPositive', v); }} />
      <Checkbox label={t('panels.constraints.stretch')} checked={values.stretch} onChange={(v) => { edit('stretch', v); }} />
      <Checkbox label={t('panels.constraints.compress')} checked={values.compress} onChange={(v) => { edit('compress', v); }} />
    </fieldset>
  );
}
